
import { Button } from "@/components/ui/button";
import { DonationField } from "./DonationField";
import { UseFormReturn } from "react-hook-form";
import { FormValues } from "../schema";
import { useState, useEffect } from "react";

interface PaymentSummaryCardProps {
  membershipType: "active" | "benefactor" | "donor";
  form: UseFormReturn<FormValues>;
  donationAmount?: number;
}

export function PaymentSummaryCard({ membershipType, form, donationAmount = 0 }: PaymentSummaryCardProps) {
  const [totalAmount, setTotalAmount] = useState<number>(membershipType === "active" ? 50 : 700);

  useEffect(() => {
    const additionalDonation = form.watch("additionalDonation") || 0;
    let base = membershipType === "active" ? 50 : 700;
    
    // Donors only pay their donation amount
    if (membershipType === "donor") {
      base = 0;
    }
    
    setTotalAmount(base + (membershipType === "donor" ? additionalDonation : additionalDonation));
    
    // For benefactors, always set includeEntryFee to true
    if (membershipType === "benefactor") {
      form.setValue("includeEntryFee", true);
    }
    
    // For donors, ensure the form value is updated with the external donationAmount
    if (membershipType === "donor" && donationAmount !== undefined) {
      form.setValue("additionalDonation", donationAmount);
      
      // Trigger validation to show error message if needed
      if (donationAmount === 0) {
        setTimeout(() => form.trigger("additionalDonation"), 100);
      }
    }
  }, [form.watch("additionalDonation"), membershipType, donationAmount, form]);

  // Calculate 66% of the total amount for tax deduction information
  const taxDeduction = Math.round(totalAmount * 0.66);
  const finalCost = totalAmount - taxDeduction;

  if (membershipType === "active") {
    return (
      <div className="rounded-lg bg-gradient-to-br from-background to-[#FCACF4]/20 p-6">
        <h3 className="text-xl font-semibold mb-4">Adhésion - Membre Actif</h3>
        <p className="text-2xl font-bold">{totalAmount} €</p>
        <p className="text-sm text-muted-foreground mt-1 font-bold">
          ne vous coûtera que {finalCost} € après déduction fiscale, un CERFA vous sera envoyé
        </p>
        <div className="text-sm mt-2 space-y-1">
          <p>- Je signe et je reçois la Charte à mon nom</p>
          <p>- Je suis informé(e) des actions à venir</p>
          <p>- J'obtiens 1 voix à l'assemblée générale</p>
          <p className="font-semibold text-[#00123A] mt-3">Je verse annuellement la somme de 50€ à titre de cotisation</p>
        </div>
        <div className="mt-4">
          <DonationField form={form} membershipType={membershipType} />
        </div>
      </div>
    );
  }

  if (membershipType === "benefactor") {
    return (
      <div className="rounded-lg bg-gradient-to-br from-background to-[#FCACF4]/20 p-6">
        <h3 className="text-xl font-semibold mb-4">Adhésion - Membre Bienfaiteur</h3>
        <p className="text-2xl font-bold">{totalAmount} €</p>
        <p className="text-sm mb-2 font-bold">Je verse un droit d'entrée de 500 euros et une cotisation annuelle de 200 euros.</p>
        <p className="text-sm text-muted-foreground mt-1 font-bold">
          ne vous coûtera que {finalCost} € après déduction fiscale, un CERFA vous sera envoyé
        </p>
        <div className="text-sm mt-2 space-y-1">
          <p>- Je signe et je reçois la Charte à mon nom</p>
          <p>- Je suis informé(e) des actions à venir</p>
          <p>- Je dispose d'un contact direct pour proposer mes initiatives</p>
          <p>- J'obtiens 5 voix à l'assemblée générale</p>
        </div>
        
        <div className="mt-4">
          <DonationField form={form} membershipType={membershipType} />
        </div>
      </div>
    );
  }

  // Donor type
  const currentDonationAmount = form.watch("additionalDonation");
  const donorTaxDeduction = Math.round(currentDonationAmount * 0.66);
  const donorFinalCost = currentDonationAmount - donorTaxDeduction;
  
  return (
    <div className="rounded-lg bg-gradient-to-br from-background to-[#FCACF4]/20 p-6">
      <h3 className="text-xl font-semibold mb-4">Votre don</h3>
      {currentDonationAmount === 0 ? (
        <p className="text-sm text-destructive">Le montant du don doit être supérieur à 0 €</p>
      ) : (
        <>
          <p className="text-2xl font-bold">{currentDonationAmount} €</p>
          <p className="text-sm text-muted-foreground mt-1">
            ne vous coûtera que {donorFinalCost} € après déduction fiscale, un CERFA vous sera envoyé
          </p>
        </>
      )}
      <p className="text-sm mt-4">
        Merci de votre générosité ! Votre don nous aidera à poursuivre nos actions 
        en faveur de l'éducation et de la protection des valeurs républicaines.
      </p>
    </div>
  );
}
